/**
 * Admin Portal → Partners → a tag → Webhook, send mode and the review queue: the view logic, kept
 * pure so jest can pin it (partner webhook intake, workstream S5).
 *
 * Contract: S1's s1-webhook-intake.md §3–§4 and the lead's answers of 2026-10-21 —
 *   - a tag's webhook (`/api/admin/partners/tags/{tag_id}/intake`): the URL shown once at create /
 *     rotate (the old URL keeps working until `previous_url_valid_until`), the consent facts, an
 *     optional opt-in check, the field map `{our field: their path}` (or `{value}` for the event
 *     fields), Enabled, and the recent deliveries — keys and counts, never a value;
 *   - send mode (`contact_send_mode`: review | automatic) is written on the tag PATCH only;
 *     Automatic covers only webhook and API arrivals after the switch — the people already waiting
 *     stay in the review queue;
 *   - a delivery's collapsed array key (`answers.[].value`) maps as `answers.0.value`;
 *   - emergency-contact and medical fields are never stored, unconditionally (owner, 2026-10-21).
 *
 * Web-only (the admin SPA).
 */

// ── the vocabulary ───────────────────────────────────────────────────────────

export const INTAKE_FIELDS = ['email', 'first_name', 'last_name', 'event_name', 'event_date', 'race_name', 'external_ref'] as const;
export type IntakeField = (typeof INTAKE_FIELDS)[number];

export const INTAKE_FIELD_LABELS: Record<IntakeField, string> = {
  email: 'Email',
  first_name: 'First name',
  last_name: 'Last name',
  event_name: 'Event',
  event_date: 'Event date',
  race_name: 'Race',
  external_ref: 'Their registration id',
};

/** Only these may be a constant for every person (a tag is often one event). */
export const CONSTANT_FIELDS = ['event_name', 'event_date', 'race_name'] as const;
/** A webhook can't be switched on without these mapped (S1's CHECK). */
export const REQUIRED_FIELDS: readonly IntakeField[] = ['email', 'first_name'];

/** partner_tag_intake's CHECKs (S1, migration 2090). */
export const INTAKE_CONSENT_LIMITS = { consent_source: 500, consent_wording: 4000, consent_wording_version: 200 } as const;
export const OPTIN_MAX = 200;
export const PATH_MAX = 200;
export const CONSTANT_MAX = 200;

export type SendMode = 'review' | 'automatic';

export type FieldMapValue = string | { value: string };

/** `GET /tags/{tag_id}/intake` → `{intake}` (never the URL, its secret or its hash). */
export interface IntakeRow {
  id: string;
  enabled: boolean;
  field_map: Partial<Record<IntakeField, FieldMapValue>>;
  optin_path: string | null;
  optin_value: string | null;
  consent_source: string | null;
  consent_wording: string | null;
  consent_wording_version: string | null;
  contact_send_mode: SendMode;
  url_issued_at: string | null;
  rotated_at: string | null;
  previous_url_valid_until: string | null;
  last_delivery_at: string | null;
}

export interface DeliveryKey {
  path: string;
  use: string;
}

/** One delivery as the log keeps it: counts, the keys seen and their use, problems by index — no value. */
export interface IntakeDelivery {
  received_at: string;
  status_code: number;
  intake_outcome: string;
  records_in: number;
  created_n: number;
  updated_n: number;
  suppressed_n: number;
  duplicate_n: number;
  rejected_n: number;
  conflict_n: number;
  not_opted_in_n: number;
  keys: DeliveryKey[];
  problems: Array<{ index: number; reason: string }>;
}

// ── the delivery log in words ────────────────────────────────────────────────

const DROPPED_LABELS: Record<string, string> = {
  payment: 'payment details',
  secret: 'a password or secret',
  government_id: 'a government ID',
  emergency_contact: 'emergency contact (never stored)',
  medical: 'medical (never stored)',
  over_cap: 'over the size cap',
};

/** What happened to one key of a delivery. */
export function keyUseLabel(use: string): string {
  if ((INTAKE_FIELDS as readonly string[]).includes(use)) return INTAKE_FIELD_LABELS[use as IntakeField];
  if (use === 'extra') return 'Kept as an extra field';
  if (use === 'opt_in') return 'Opt-in check';
  if (use.startsWith('dropped:')) {
    const cat = use.slice('dropped:'.length);
    return `Dropped: ${DROPPED_LABELS[cat] ?? cat.replace(/_/g, ' ')}`;
  }
  return use.replace(/_/g, ' ');
}

const OUTCOME_LABELS: Record<string, string> = {
  accepted: 'Accepted',
  bad_json: 'Not JSON',
  too_large: 'Too large',
  too_many_records: 'Too many records',
  error: 'Error (the platform retries)',
};

export function outcomeLabel(outcome: string): string {
  return OUTCOME_LABELS[outcome] ?? outcome.replace(/_/g, ' ');
}

/** "3 in · 2 new · 1 rejected" — the non-zero counts only. */
export function deliveryCountsText(d: Pick<IntakeDelivery, 'records_in' | 'created_n' | 'updated_n' | 'suppressed_n' |
  'duplicate_n' | 'rejected_n' | 'conflict_n' | 'not_opted_in_n'>): string {
  const parts = [`${Number(d.records_in || 0).toLocaleString('en-CA')} in`];
  const add = (n: number, word: string) => { if (Number(n) > 0) parts.push(`${Number(n).toLocaleString('en-CA')} ${word}`); };
  add(d.created_n, 'new');
  add(d.updated_n, 'updated');
  add(d.suppressed_n, 'never-email');
  add(d.duplicate_n, 'repeated');
  add(d.rejected_n, 'rejected');
  add(d.conflict_n, 'conflicting');
  add(d.not_opted_in_n, 'not opted in');
  return parts.join(' · ');
}

/** A delivery's problems in words, never a value. A one-record delivery needs no record number. */
export function problemLines(d: Pick<IntakeDelivery, 'records_in' | 'problems'>): string[] {
  const list = d.problems || [];
  if (Number(d.records_in) === 1) return list.map((p) => p.reason);
  return list.map((p) => `Record ${p.index}: ${p.reason}`);
}

/** The keys a field-map picker offers: the paths the last delivery carried, in its order, once each. */
export function seenPaths(deliveries: IntakeDelivery[]): string[] {
  const last = deliveries[0];
  if (!last) return [];
  const out: string[] = [];
  for (const k of last.keys || []) if (k?.path && !out.includes(k.path)) out.push(k.path);
  return out;
}

/** The log collapses array indexes to `[]`; the field map takes a real index — offer 0. */
export function concretePath(path: string): string {
  return String(path ?? '')
    .split('.')
    .map((seg) => (seg === '[]' ? '0' : seg))
    .join('.');
}

// ── the field map ────────────────────────────────────────────────────────────

export interface FieldMapEntry {
  mode: 'none' | 'path' | 'value';
  path: string;
  value: string;
}
export type FieldMapForm = Record<IntakeField, FieldMapEntry>;

export function emptyFieldMapForm(): FieldMapForm {
  return Object.fromEntries(INTAKE_FIELDS.map((f) => [f, { mode: 'none', path: '', value: '' }])) as FieldMapForm;
}

export function fieldMapFormFrom(map: IntakeRow['field_map'] | null | undefined): FieldMapForm {
  const form = emptyFieldMapForm();
  for (const f of INTAKE_FIELDS) {
    const v = map?.[f];
    if (typeof v === 'string') form[f] = { mode: 'path', path: v, value: '' };
    else if (v && typeof v === 'object' && typeof v.value === 'string') form[f] = { mode: 'value', path: '', value: v.value };
  }
  return form;
}

/** A dot path the server will read: non-empty segments, no collapsed `[]` marker, no spaces at the ends. */
export function pathError(path: string): string | null {
  if (path.length > PATH_MAX) return `at most ${PATH_MAX} characters`;
  const segs = path.split('.');
  if (segs.some((s) => !s.trim())) return 'a dot path with no empty parts, e.g. participant.email';
  if (segs.some((s) => s.includes('[') || s.includes(']'))) return 'use a number for an array position (answers.0.value), not []';
  return null;
}

/** The field map S1 stores: `{field: path}` or `{field: {value}}` (event fields only). */
export function fieldMapBody(form: FieldMapForm): { map: IntakeRow['field_map']; errors: string[] } {
  const map: IntakeRow['field_map'] = {};
  const errors: string[] = [];
  for (const f of INTAKE_FIELDS) {
    const e = form[f];
    const name = INTAKE_FIELD_LABELS[f];
    if (e.mode === 'path') {
      const p = e.path.trim();
      if (!p) { errors.push(`${name}: type the path, or choose "Not mapped".`); continue; }
      const err = pathError(p);
      if (err) { errors.push(`${name}: ${err}.`); continue; }
      map[f] = p;
    } else if (e.mode === 'value') {
      if (!(CONSTANT_FIELDS as readonly string[]).includes(f)) {
        errors.push(`${name}: must come from each person's record, not a fixed value.`);
        continue;
      }
      const v = e.value.trim();
      if (!v) { errors.push(`${name}: type the fixed value, or choose "Not mapped".`); continue; }
      if (v.length > CONSTANT_MAX) { errors.push(`${name}: at most ${CONSTANT_MAX} characters.`); continue; }
      map[f] = { value: v };
    }
  }
  return { map, errors };
}

// ── consent, the opt-in check, Enabled ───────────────────────────────────────

export interface IntakeConsentForm {
  consent_source: string;
  consent_wording: string;
  consent_wording_version: string;
  optin_path: string;
  optin_value: string;
}

export function intakeConsentFormFrom(i: IntakeRow | null): IntakeConsentForm {
  return {
    consent_source: i?.consent_source ?? '',
    consent_wording: i?.consent_wording ?? '',
    consent_wording_version: i?.consent_wording_version ?? '',
    optin_path: i?.optin_path ?? '',
    optin_value: i?.optin_value ?? '',
  };
}

const CONSENT_NAMES: Record<keyof typeof INTAKE_CONSENT_LIMITS, string> = {
  consent_source: 'Platform name',
  consent_wording: 'The "Yes" box wording',
  consent_wording_version: 'Its version',
};

export function intakeConsentErrors(f: IntakeConsentForm): string[] {
  const errors: string[] = [];
  for (const k of Object.keys(INTAKE_CONSENT_LIMITS) as Array<keyof typeof INTAKE_CONSENT_LIMITS>) {
    const max = INTAKE_CONSENT_LIMITS[k];
    if ((f[k] ?? '').trim().length > max) errors.push(`${CONSENT_NAMES[k]}: at most ${max.toLocaleString('en-CA')} characters.`);
  }
  const op = (f.optin_path ?? '').trim();
  const ov = (f.optin_value ?? '').trim();
  if (!!op !== !!ov) errors.push('The opt-in check needs its path and its value together (or neither).');
  if (op) {
    const err = pathError(op);
    if (err) errors.push(`Opt-in path: ${err}.`);
  }
  if (op.length > OPTIN_MAX || ov.length > OPTIN_MAX) errors.push(`The opt-in path and value: at most ${OPTIN_MAX} characters each.`);
  return errors;
}

/** What stands between this webhook and Enabled — the screen keeps the switch off until it is empty. */
export function enableBlockers(i: Pick<IntakeRow, 'consent_source' | 'consent_wording' | 'consent_wording_version' | 'field_map'>): string[] {
  const out: string[] = [];
  if (!(i.consent_source ?? '').trim()) out.push('the platform name');
  if (!(i.consent_wording ?? '').trim()) out.push('the exact wording of the "Yes" box');
  if (!(i.consent_wording_version ?? '').trim()) out.push('that wording’s version');
  if (typeof i.field_map?.email !== 'string' || !i.field_map.email) out.push('a path for the email address');
  if (typeof i.field_map?.first_name !== 'string' || !i.field_map.first_name) out.push('a path for the first name');
  return out;
}

/** `PATCH /tags/{tag_id}/intake`: only what changed (empty text → null), the field map whole when any of it moved. */
export function intakePatchBody(original: IntakeRow, consent: IntakeConsentForm, map: FieldMapForm): {
  body: Record<string, unknown>;
  errors: string[];
} {
  const errors = intakeConsentErrors(consent);
  const body: Record<string, unknown> = {};
  for (const k of ['consent_source', 'consent_wording', 'consent_wording_version', 'optin_path', 'optin_value'] as const) {
    const next = (consent[k] ?? '').trim() || null;
    if (next !== ((original[k] ?? '').trim() || null)) body[k] = next;
  }
  const fm = fieldMapBody(map);
  errors.push(...fm.errors);
  const before = JSON.stringify(fieldMapBody(fieldMapFormFrom(original.field_map)).map);
  if (JSON.stringify(fm.map) !== before) body.field_map = fm.map;
  return { body, errors };
}

// ── send mode ────────────────────────────────────────────────────────────────

export function sendModeLabel(mode: SendMode | null | undefined): string {
  return mode === 'automatic' ? 'Send automatically' : 'Review first';
}

/** The Automatic confirmation (the lead's wording, 2026-10-21), with the waiting count. */
export function sendModeConfirmText(tag: string, waiting: number): string {
  const n = Number(waiting || 0);
  const stay = n === 0
    ? 'Nobody is waiting right now.'
    : n === 1
      ? 'The 1 person already waiting stays here for you to review.'
      : `The ${n.toLocaleString('en-CA')} already waiting stay here for you to review.`;
  return `${tag}: From now on, new people from this link's webhook and API get the invitation automatically. ${stay} ` +
    'A CSV upload never sends by itself.';
}
